export type ProfileDateFormat = "dmy" | "mdy";
export type ProfileDefaultView =
  | "dashboard"
  | "project_stats"
  | "people_stats"
  | "research"
  | "ideas"
  | "experiments"
  | "reports"
  | "to_do_lists"
  | "brainstorm_ideas"
  | "roadmap"
  | "query_engine";

export interface IUser {
  id: string;
  name: string;
  company_name: string;
  current_plan: string;
  user_role: UserRole;
  avatar: string;
  best_role: UserRole;
  beta: boolean;
  master_account_status: string;
  surname: string;
  username: string;
  intercom_id: string;
  email: string;
  demo_project_id: string;
  roles: UserRole[];
  new_ideas_preference: boolean;
  new_experiments_preference: boolean;
  date_format: {
    input: string;
    short: string;
    long: string;
    name: string;
    value: ProfileDateFormat;
  };
  roadmap_token: string;
  is_master: boolean;
  is_active: boolean;
  is_trial: boolean;
  super_admin: boolean;
  trial_data: {
    interval: number;
    behind: number;
    days: number;
    is_active: boolean;
  };
  show_starting_position_dialog: boolean;
  starting_position_path: ProfileDefaultView;
  starting_position_project_id?: string;
  can_create_tag: {
    [projectId: string]: boolean;
  };
  session_token: string;
  created_at: string;
  feature_modules: {
    enabled: UserFeatureModuleKey[];
    disabled: UserFeatureModuleKey[];
  };
  subdomains: string[];
  onboarding: Record<UserRole, boolean>;
  project_ids_as_client: string[];
  accounts: Array<{
    id: number;
    name: string;
    logo: string | null;
    subdomain: string;
    is_active: boolean;
    fiscal_year_start_month: number;
    master: {
      id: string;
      name: string;
      avatar?: string;
    };
  }>;
}

export interface IAccountUser {
  id: string;
  name: string;
  email: string;
  username: string;
  state: "active" | "pending";
  best_role: UserRole;
  project_zaps_count: Record<string, number>;
  projects: {
    data: Array<{
      id: string;
      type: string;
      attributes: {
        id: string;
        name: string;
        role: UserRole;
      };
    }>;
  };
}

export type UserRole =
  | "master"
  | "executive"
  | "program_manager"
  | "administrator"
  | "staff"
  | "client"
  | "team_viewer"
  | "client_viewer";

export interface IProjectUser {
  id: string;
  name: string;
  surname: string;
  username: string;
  avatar: string;
  user_projects: Array<[string, [string]]>;
  email: string;
  created_at: string;
}

export enum UserFeatureModuleKey {
  EXPERIMENT_SCORECARD = "experiment_scorecard",
  CROWDSOURCE_V2 = "crowdsource_v2",
  IDEAS_V2 = "ideas_v2",
  RESEARCH_PRIORITISATION = "research_prioritisation",
  RESEARCH_V2 = "research_v2",
  REPORTS = "reports",
  TODOS_V2 = "todos_v2",
  INSIGHTS_ENGINE_V2 = "insights_engine_v2",
  PROJECT_STATS_V2 = "project_stats_v2",
  DASHBOARD_V2 = "dashboard_v2",
  PUBLIC_ROADMAP = "public_roadmap",
  TAG_MANAGER_V2 = "tag_manager_v2",
  GOAL_MANAGER_V2 = "goal_manager_v2",
  PEOPLE_STATS = "people_stats",
  USER_DETAILS = "user_details",
  DEPENDENCIES = "dependencies",
  TEAMS = "teams",
  ROADMAP = "roadmap",
  JIRA = "jira",
  JIRA_PRO = "jira_pro",
  SLACK = "slack",
  MICROSOFT_TEAMS = "microsoft_teams",
  KAMELEOON = "kameleoon",
  OPTIMIZELY = "optimizely",
  VWO = "vwo",
  AB_TASTY = "ab_tasty",
  GOOGLE_ANALYTICS = "google_analytics",
  GOOGLE_DRIVE = "google_drive",
  ZAPIER = "zapier",
  PLAYBOOKS = "playbooks",
  OBJECTIVES = "objectives",
  MANUAL_METRIC_OBJECTIVES = "manual_metric_objectives",
  AUTO_METRIC_OBJECTIVES = "auto_metric_objectives",
  LEARNING_OBJECTIVES = "learning_objectives",
  PROJECT_GROUPING = "project_grouping",
  WATCHTOWER = "watchtower",
  IMPORT_TEMPLATES = "import_templates",
  IMPORT_TAGS = "import_tags",
  IMPORT_IDEAS = "import_ideas",
  IMPORT_EXPERIMENTS = "import_experiments",
  AI = "ai",
  AI_CHAT = "ai_chat",
  SEMANTIC_SEARCH = "semantic_search"
}

export type FeatureModule = {
  id: number;
  feature_id: number;
  title: string;
  key: UserFeatureModuleKey;
  beta: boolean;
  user_controlled: boolean;
  is_open_to_all: boolean;
  created_at: string;
  updated_at: string;
};

export type FeatureModuleAccess = {
  enabled: UserFeatureModuleKey[];
  disabled: UserFeatureModuleKey[];
};

export interface IExternalUser {
  id: string;
  name: string;
  email: string;
  role: string;
}
